import React, { createContext, useContext, useState, ReactNode } from "react"
import Notification, { NotificationContainer } from "~/components/ui/Notification"

export type NotificationType = "success" | "error" | "warning" | "info"

export interface INotification {
  id: string
  type: NotificationType
  title: string
  description?: string
  dismissAfter?: number // in milliseconds
  onClick?: () => void
  showTimer?: boolean
}

type NotificationContextType = {
  notifications: INotification[]
  showNotification: (notification: Omit<INotification, "id">) => void
  hideNotification: (id: string) => void
}

const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  showNotification: () => {},
  hideNotification: () => {},
})

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<INotification[]>([])

  const showNotification = (notification: Omit<INotification, "id">) => {
    // Create a unique ID (using timestamp; in a real app, consider a more robust method)
    const id = String(Date.now())
    setNotifications((prev) => [...prev, { ...notification, id }])
    if (notification.dismissAfter) {
      setTimeout(() => {
        hideNotification(id)
      }, notification.dismissAfter)
    }
  }

  const hideNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }

  return (
    <NotificationContext.Provider value={{ notifications, showNotification, hideNotification }}>
      {children}
      <NotificationContainer>
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            visible={true}
            title={notification.title}
            description={notification.description}
            onDismiss={() => hideNotification(notification.id)}
            dismissAfter={notification.dismissAfter}
            onClick={notification.onClick}
            showTimer={notification.showTimer}
            // Pass additional props based on type
            {...(notification.type === "success" && { success: true })}
            {...(notification.type === "error" && { error: true })}
            {...(notification.type === "warning" && { iconWarning: true })}
          />
        ))}
      </NotificationContainer>
    </NotificationContext.Provider>
  )
}

export const useNotification = () => useContext(NotificationContext)
